import React, { useState, useMemo, useEffect } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import Navbar from '../../../shared/components/Navbar';
import Footer from '../../../shared/components/Footer';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import Card from '../../../shared/components/Card';

const Changelog: React.FC = () => {
  const { t, loading } = usePageTranslation('public', 'Changelog');
  const [filter, setFilter] = useState<'all' | 'major' | 'minor' | 'patch'>('all');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Détecter le scroll pour afficher/masquer le bouton
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fonction pour remonter en haut
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getUpdateTypeColor = (type: string) => {
    switch (type) {
      case 'major':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
      case 'minor':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700';
      case 'patch':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    }
  };

  const getUpdateTypeText = (type: string) => {
    switch (type) {
      case 'major':
        return t('types.major');
      case 'minor':
        return t('types.minor');
      case 'patch':
        return t('types.patch');
      default:
        return type;
    }
  };

  // Récupération des updates avec useMemo pour éviter les recalculs
  const updates = useMemo(() => {
    if (loading) return [];
    
    const updatesRaw = t('updates');
    
    // Debug: vérifier ce qui est retourné
    if (process.env.NODE_ENV === 'development') {
      console.log('Changelog - updatesRaw:', updatesRaw);
      console.log('Changelog - type:', typeof updatesRaw);
      console.log('Changelog - isArray:', Array.isArray(updatesRaw));
      if (updatesRaw && typeof updatesRaw === 'object') {
        console.log('Changelog - updatesRaw keys:', Object.keys(updatesRaw));
      }
    }
    
    // Si c'est une chaîne (clé non trouvée), retourner un tableau vide
    if (typeof updatesRaw === 'string' && updatesRaw === 'updates') {
      console.warn('Changelog: updates key not found in translations');
      return [];
    }
    
    // Si c'est un tableau valide
    if (Array.isArray(updatesRaw) && updatesRaw.length > 0) {
      return updatesRaw as Array<{
        date: string;
        version: string;
        type: 'major' | 'minor' | 'patch';
        title: string;
        changes: string[];
      }>;
    }
    
    return [];
  }, [t, loading]);

  const filteredUpdates = filter === 'all' 
    ? updates 
    : updates.filter(update => update.type === filter);

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <Navbar />
      <div className="relative z-10">
        {/* Header de la page */}
        <section className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 text-gradient">
                {t('title')}
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {t('subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Filtres */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {t('filters.all')}
              </button>
              <button
                onClick={() => setFilter('major')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'major'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {t('filters.major')}
              </button>
              <button
                onClick={() => setFilter('minor')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'minor'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {t('filters.minor')}
              </button>
              <button
                onClick={() => setFilter('patch')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  filter === 'patch'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {t('filters.patch')}
              </button>
            </div>
          </div>
        </section>

        {/* Liste des mises à jour */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {loading ? (
                <Card className="p-8 md:p-12 text-center">
                  <p className="text-gray-400 text-lg">Chargement...</p>
                </Card>
              ) : filteredUpdates.length > 0 ? (
                filteredUpdates.map((update, index) => (
                  <Card key={index} className="p-8 md:p-12">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {update.title}
                        </h3>
                        <p className="text-gray-400">
                          Version {update.version} - {formatDate(update.date)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border mt-4 md:mt-0 ${getUpdateTypeColor(update.type)}`}>
                        {getUpdateTypeText(update.type)}
                      </span>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-4">{t('changes')}:</h4>
                      <ul className="space-y-3">
                        {update.changes.map((change: string, changeIndex: number) => (
                          <li key={changeIndex} className="flex items-start text-gray-300">
                            <span className="text-primary-400 mr-3 mt-1">•</span>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                ))
              ) : (
                <Card className="p-8 md:p-12 text-center">
                  <p className="text-gray-400 text-lg">{t('noUpdates')}</p>
                </Card>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Bouton pour remonter en haut */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Remonter en haut de la page"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Changelog;

